import React, { Component } from "react";
import {
  Table,
  Row,
  Col,
  FormControl,
  Button,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import CartContext from "../StoreManager/CartContext";
import { fetchCrmList, saveCrmItem, deleteCrmItem } from "./CrmApi";

const emptyAppointment = {
  appointmentId: "",
  storeId: "",
  contactId: "",
  assignedUserId: "",
  appointmentType: "",
  startTime: "",
  endTime: "",
  status: "",
  location: "",
  notes: "",
};

const emptyNewCustomer = {
  firstName: "",
  lastName: "",
  companyName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  status: "Customer",
  contactType: "Individual",
};

const appointmentStatusOptions = [
  "Scheduled",
  "Completed",
  "Cancelled",
  "Rescheduled",
  "No Show",
];
const appointmentTypeOptions = [
  "Consultation",
  "Follow-up",
  "Onsite",
  "Phone Call",
  "Virtual",
];

export default class CrmAppointments extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      appointments: [],
      contacts: [],
      filteredAppointments: [],
      loading: true,
      search: "",
      statusFilter: "",
      typeFilter: "",
      fromDate: "",
      toDate: "",
      error: null,
      showModal: false,
      isEdit: false,
      contactSelectionMode: "existing",
      newCustomer: { ...emptyNewCustomer },
      currentAppointment: { ...emptyAppointment },
    };
  }

  componentDidMount() {
    this.loadAppointments();
    this.loadContacts();
  }

  loadContacts = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      return;
    }

    try {
      const contacts = await fetchCrmList("Contacts", "GetContacts", storeId);
      const normalizedContacts = Array.isArray(contacts) ? contacts : [];
      this.setState({ contacts: normalizedContacts });
    } catch (error) {
      // Keep appointments working even if contact list fails
      console.warn("Unable to load CRM contacts", error);
    }
  };

  loadAppointments = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ loading: false, error: "StoreId is required." });
      return;
    }

    this.setState({ loading: true, error: null });
    try {
      const appointments = await fetchCrmList(
        "Appointments",
        "GetAppointments",
        storeId,
      );
      const normalized = Array.isArray(appointments) ? appointments : [];
      this.setState({
        appointments: normalized,
        filteredAppointments: normalized,
        loading: false,
      });
    } catch (error) {
      this.setState({
        loading: false,
        error: error.message || "Unable to load appointments.",
      });
    }
  };

  filterAppointments = ({
    search,
    statusFilter,
    typeFilter,
    fromDate,
    toDate,
  }) => {
    const normalizedSearch = (search || "").trim().toLowerCase();
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;

    return this.state.appointments.filter((appointment) => {
      const subject = (
        appointment.subject ||
        appointment.title ||
        ""
      ).toLowerCase();
      const location = (appointment.location || "").toLowerCase();
      const status = (appointment.status || "").toLowerCase();
      const type = (appointment.appointmentType || "").toLowerCase();
      const contactId = (appointment.contactId || "").toString().toLowerCase();
      const assignedUserId = (appointment.assignedUserId || "")
        .toString()
        .toLowerCase();
      const contactName = (appointment.contactName || "").toLowerCase();
      const start = appointment.startTime || appointment.start || "";
      const startDate = start ? new Date(start) : null;

      const searchMatch =
        subject.includes(normalizedSearch) ||
        location.includes(normalizedSearch) ||
        status.includes(normalizedSearch) ||
        contactId.includes(normalizedSearch) ||
        assignedUserId.includes(normalizedSearch) ||
        contactName.includes(normalizedSearch);

      const statusMatch =
        !statusFilter || status === statusFilter.toLowerCase();
      const typeMatch = !typeFilter || type === typeFilter.toLowerCase();
      const fromMatch = !from || (startDate && startDate >= from);
      const toMatch = !to || (startDate && startDate <= to);

      return searchMatch && statusMatch && typeMatch && fromMatch && toMatch;
    });
  };

  onSearchChange = (event) => {
    const search = event.target.value || "";
    const { statusFilter, typeFilter, fromDate, toDate } = this.state;
    this.setState({
      search,
      filteredAppointments: this.filterAppointments({
        search,
        statusFilter,
        typeFilter,
        fromDate,
        toDate,
      }),
    });
  };

  onStatusFilterChange = (event) => {
    const statusFilter = event.target.value;
    const { search, typeFilter, fromDate, toDate } = this.state;
    this.setState({
      statusFilter,
      filteredAppointments: this.filterAppointments({
        search,
        statusFilter,
        typeFilter,
        fromDate,
        toDate,
      }),
    });
  };

  onTypeFilterChange = (event) => {
    const typeFilter = event.target.value;
    const { search, statusFilter, fromDate, toDate } = this.state;
    this.setState({
      typeFilter,
      filteredAppointments: this.filterAppointments({
        search,
        statusFilter,
        typeFilter,
        fromDate,
        toDate,
      }),
    });
  };

  onFromDateChange = (event) => {
    const fromDate = event.target.value;
    const { search, statusFilter, typeFilter, toDate } = this.state;
    this.setState({
      fromDate,
      filteredAppointments: this.filterAppointments({
        search,
        statusFilter,
        typeFilter,
        fromDate,
        toDate,
      }),
    });
  };

  onToDateChange = (event) => {
    const toDate = event.target.value;
    const { search, statusFilter, typeFilter, fromDate } = this.state;
    this.setState({
      toDate,
      filteredAppointments: this.filterAppointments({
        search,
        statusFilter,
        typeFilter,
        fromDate,
        toDate,
      }),
    });
  };

  formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString();
  };

  handleShowModal = (appointment = null) => {
    this.setState({
      currentAppointment: appointment
        ? { ...appointment }
        : { ...emptyAppointment },
      isEdit: Boolean(appointment),
      showModal: true,
      error: null,
      contactSelectionMode: "existing",
      newCustomer: { ...emptyNewCustomer },
    });
  };

  handleContactModeChange = (mode) => {
    this.setState({ contactSelectionMode: mode });
  };

  handleNewCustomerFieldChange = (field, value) => {
    this.setState((prevState) => ({
      newCustomer: {
        ...prevState.newCustomer,
        [field]: value,
      },
    }));
  };

  handleCloseModal = () => {
    this.setState({ showModal: false, error: null });
  };

  handleFieldChange = (field, value) => {
    this.setState((prevState) => ({
      currentAppointment: {
        ...prevState.currentAppointment,
        [field]: value,
      },
    }));
  };

  getContactDisplayName = (contactId) => {
    const contact = this.state.contacts.find(
      (item) => item.contactId === contactId,
    );
    if (!contact) return contactId || "-";
    const name = `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
    return name || contact.email || contact.companyName || contact.contactId;
  };

  getDuration = (appointment) => {
    const start = appointment.startTime || appointment.start;
    const end = appointment.endTime || appointment.end;
    if (!start || !end) return "-";
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()))
      return "-";
    const diffMs = endDate - startDate;
    if (diffMs <= 0) return "-";
    const diffMinutes = Math.round(diffMs / 60000);
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `${hours ? `${hours}h ` : ""}${minutes}m`;
  };

  saveAppointment = async () => {
    const storeId = this.context.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId is required." });
      return;
    }

    const appointment = {
      ...this.state.currentAppointment,
      storeId,
      appointmentId:
        this.state.currentAppointment.appointmentId ||
        "00000000-0000-0000-0000-000000000000",
      startTime: this.state.currentAppointment.startTime || null,
      endTime: this.state.currentAppointment.endTime || null,
    };

    try {
      if (this.state.contactSelectionMode === "new") {
        const newCustomer = {
          ...this.state.newCustomer,
          storeId,
          contactId: "00000000-0000-0000-0000-000000000000",
        };
        const savedContact = await saveCrmItem(
          "Contacts",
          "AddOrUpdateContact",
          newCustomer,
        );
        appointment.contactId =
          (savedContact && savedContact.contactId) || savedContact || "";
      }

      await saveCrmItem("Appointments", "AddOrUpdateAppointment", appointment);
      await this.loadContacts();
      await this.loadAppointments();
      this.handleCloseModal();
    } catch (error) {
      this.setState({ error: error.message || "Unable to save appointment." });
    }
  };

  deleteAppointment = async (appointmentId) => {
    const storeId = this.context.storeId;
    if (!storeId) return;
    if (!window.confirm("Delete this appointment?")) return;

    try {
      await deleteCrmItem(
        "Appointments",
        "DeleteAppointment",
        storeId,
        appointmentId,
      );
      await this.loadAppointments();
    } catch (error) {
      this.setState({
        error: error.message || "Unable to delete appointment.",
      });
    }
  };

  render() {
    const {
      filteredAppointments,
      loading,
      search,
      error,
      showModal,
      isEdit,
      currentAppointment,
      contactSelectionMode,
      newCustomer,
      contacts,
    } = this.state;
    const storeId = this.context.storeId;
    const totalAppointments = this.state.appointments.length;
    const scheduledCount = this.state.appointments.filter(
      (appointment) => (appointment.status || "").toLowerCase() === "scheduled",
    ).length;
    const completedCount = this.state.appointments.filter(
      (appointment) => (appointment.status || "").toLowerCase() === "completed",
    ).length;
    const upcomingCount = this.state.appointments.filter((appointment) => {
      const start = appointment.startTime || appointment.start;
      const startDate = start ? new Date(start) : null;
      return startDate && startDate > new Date();
    }).length;

    return (
      <div className="m-3">
        <Row className="align-items-center mb-3">
          <Col>
            <h2>CRM Appointments</h2>
            <p className="text-muted">
              Manage appointments for the selected store.
            </p>
          </Col>
          <Col xs="auto" className="d-flex gap-2">
            <Button variant="primary" onClick={() => this.handleShowModal()}>
              Add Appointment
            </Button>
            <Button
              variant="secondary"
              onClick={this.loadAppointments}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Refresh"}
            </Button>
          </Col>
        </Row>

        <Row className="mb-3 g-2">
          <Col xs={6} md={3} lg={2}>
            <div className="border rounded p-3 bg-light h-100">
              <div className="text-muted">Total</div>
              <div className="fs-4 fw-bold">{totalAppointments}</div>
            </div>
          </Col>
          <Col xs={6} md={3} lg={2}>
            <div className="border rounded p-3 bg-light h-100">
              <div className="text-muted">Scheduled</div>
              <div className="fs-4 fw-bold">{scheduledCount}</div>
            </div>
          </Col>
          <Col xs={6} md={3} lg={2}>
            <div className="border rounded p-3 bg-light h-100">
              <div className="text-muted">Completed</div>
              <div className="fs-4 fw-bold">{completedCount}</div>
            </div>
          </Col>
          <Col xs={6} md={3} lg={2}>
            <div className="border rounded p-3 bg-light h-100">
              <div className="text-muted">Upcoming</div>
              <div className="fs-4 fw-bold">{upcomingCount}</div>
            </div>
          </Col>
        </Row>

        {!storeId && (
          <div className="alert alert-warning">
            StoreId is required to load CRM appointments.
          </div>
        )}
        {error && <div className="alert alert-danger">{error}</div>}

        <Row className="mb-3 g-2">
          <Col md={6} lg={4}>
            <FormControl
              placeholder="Search by subject, contactId, location, or status"
              value={search}
              onChange={this.onSearchChange}
            />
          </Col>
          <Col md={3} lg={2}>
            <Form.Select
              value={this.state.typeFilter}
              onChange={this.onTypeFilterChange}
            >
              <option value="">All types</option>
              {appointmentTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3} lg={2}>
            <Form.Select
              value={this.state.statusFilter}
              onChange={this.onStatusFilterChange}
            >
              <option value="">All statuses</option>
              {appointmentStatusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={3} lg={2}>
            <Form.Control
              type="date"
              value={this.state.fromDate}
              onChange={this.onFromDateChange}
              placeholder="From"
            />
          </Col>
          <Col md={3} lg={2}>
            <Form.Control
              type="date"
              value={this.state.toDate}
              onChange={this.onToDateChange}
              placeholder="To"
            />
          </Col>
          <Col md={3} lg={2} className="text-end align-self-center">
            <small className="text-muted">
              Showing {filteredAppointments.length} of{" "}
              {this.state.appointments.length}
            </small>
          </Col>
        </Row>

        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Type</th>
              <th>Contact</th>
              <th>Assigned User</th>
              <th>Start</th>
              <th>End</th>
              <th>Location</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="text-center py-5">
                  <Spinner animation="border" /> Loading...
                </td>
              </tr>
            ) : filteredAppointments.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center py-5">
                  No appointments found.
                </td>
              </tr>
            ) : (
              filteredAppointments.map((appointment) => (
                <tr key={appointment.appointmentId || Math.random()}>
                  <td>{appointment.subject || "-"}</td>
                  <td>{appointment.appointmentType || "-"}</td>
                  <td>{appointment.contactId || "-"}</td>
                  <td>{appointment.assignedUserId || "-"}</td>
                  <td>
                    {this.formatDateTime(
                      appointment.startTime || appointment.start,
                    )}
                  </td>
                  <td>
                    {this.formatDateTime(
                      appointment.endTime || appointment.end,
                    )}
                  </td>
                  <td>{appointment.location || "-"}</td>
                  <td>{appointment.status || "-"}</td>
                  <td>{this.getDuration(appointment)}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => this.handleShowModal(appointment)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() =>
                        this.deleteAppointment(appointment.appointmentId)
                      }
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={this.handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {isEdit ? "Edit Appointment" : "Add Appointment"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="appointmentSubject">
                    <Form.Label>Subject</Form.Label>
                    <Form.Control
                      value={currentAppointment.subject}
                      onChange={(e) =>
                        this.handleFieldChange("subject", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="appointmentType">
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      value={currentAppointment.appointmentType}
                      onChange={(e) =>
                        this.handleFieldChange(
                          "appointmentType",
                          e.target.value,
                        )
                      }
                    >
                      <option value="">Select type</option>
                      {appointmentTypeOptions.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="appointmentStatus">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={currentAppointment.status}
                      onChange={(e) =>
                        this.handleFieldChange("status", e.target.value)
                      }
                    >
                      <option value="">Select status</option>
                      {appointmentStatusOptions.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="appointmentStart">
                    <Form.Label>Start Time</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={
                        currentAppointment.startTime?.substring(0, 16) || ""
                      }
                      onChange={(e) =>
                        this.handleFieldChange("startTime", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="appointmentEnd">
                    <Form.Label>End Time</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      value={currentAppointment.endTime?.substring(0, 16) || ""}
                      onChange={(e) =>
                        this.handleFieldChange("endTime", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="contactMode">
                    <Form.Label>Customer</Form.Label>
                    <div className="d-flex gap-2">
                      <Button
                        variant={
                          contactSelectionMode === "existing"
                            ? "primary"
                            : "outline-secondary"
                        }
                        size="sm"
                        onClick={() => this.handleContactModeChange("existing")}
                      >
                        Existing
                      </Button>
                      <Button
                        variant={
                          contactSelectionMode === "new"
                            ? "primary"
                            : "outline-secondary"
                        }
                        size="sm"
                        onClick={() => this.handleContactModeChange("new")}
                      >
                        Register New
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
                {contactSelectionMode === "existing" ? (
                  <>
                    <Col md={6}>
                      <Form.Group controlId="appointmentContactId">
                        <Form.Label>Select Existing Customer</Form.Label>
                        <Form.Select
                          value={currentAppointment.contactId}
                          onChange={(e) =>
                            this.handleFieldChange("contactId", e.target.value)
                          }
                        >
                          <option value="">Choose customer</option>
                          {contacts.map((contact) => {
                            const name =
                              `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
                            return (
                              <option
                                key={contact.contactId}
                                value={contact.contactId}
                              >
                                {name ||
                                  contact.email ||
                                  contact.companyName ||
                                  contact.contactId}
                              </option>
                            );
                          })}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="appointmentAssignedUserId">
                        <Form.Label>Assigned User Id</Form.Label>
                        <Form.Control
                          value={currentAppointment.assignedUserId}
                          onChange={(e) =>
                            this.handleFieldChange(
                              "assignedUserId",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </>
                ) : (
                  <>
                    <Col md={6}>
                      <Form.Group controlId="newCustomerEmail">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={newCustomer.email}
                          onChange={(e) =>
                            this.handleNewCustomerFieldChange(
                              "email",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="newCustomerPhone">
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          value={newCustomer.phone}
                          onChange={(e) =>
                            this.handleNewCustomerFieldChange(
                              "phone",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="newCustomerFirstName">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          value={newCustomer.firstName}
                          onChange={(e) =>
                            this.handleNewCustomerFieldChange(
                              "firstName",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="newCustomerLastName">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          value={newCustomer.lastName}
                          onChange={(e) =>
                            this.handleNewCustomerFieldChange(
                              "lastName",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="newCustomerCompanyName">
                        <Form.Label>Company</Form.Label>
                        <Form.Control
                          value={newCustomer.companyName}
                          onChange={(e) =>
                            this.handleNewCustomerFieldChange(
                              "companyName",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="newCustomerCountry">
                        <Form.Label>Country</Form.Label>
                        <Form.Control
                          value={newCustomer.country}
                          onChange={(e) =>
                            this.handleNewCustomerFieldChange(
                              "country",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
                <Col md={6}>
                  <Form.Group controlId="appointmentLocation">
                    <Form.Label>Location</Form.Label>
                    <Form.Control
                      value={currentAppointment.location}
                      onChange={(e) =>
                        this.handleFieldChange("location", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="appointmentNotes">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={currentAppointment.notes}
                      onChange={(e) =>
                        this.handleFieldChange("notes", e.target.value)
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" onClick={this.saveAppointment}>
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
